import { useLivePoll } from '@polf/shared/hooks'
import { useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  type HoleScoreFragment,
  useCurrentPlayerFlightMatchesQuery,
  useCurrentPlayerFlightScoresQuery,
} from '../../__generated__/hooks'
import Toggle from '../../components/Leaderboard/Toggle'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import type { CurrentPlayerCtx } from '../../PlayerShell'
import { isBestBallFlight } from '../../util/best-ball'
import {
  isBestWorst,
  isOneBallTeamMode,
  isSegmented,
} from '../../util/event-mode'
import BestBallView from './views/BestBallView'
import IndividualView from './views/IndividualView'
import MatchView from './views/MatchView'
import SegmentedView from './views/SegmentedView'

/**
 * Traditional Norwegian scorecard, rebuilt for mobile.
 *
 * The printed A5-landscape card has holes across columns and players
 * down rows. On a phone that means horizontal scroll past hole 9, with
 * tiny cells that can't carry proper hand-drawn score marks. We flip
 * the axis: holes go down (vertical scroll is the mobile-native axis),
 * players go across. Marks render at ink-size (40–48px); the card fits
 * any phone width without horizontal pan.
 *
 * Mark grammar follows the broadcast/TV convention (see
 * `components/ScoreMark.tsx`) rather than the Norwegian paper
 * convention — better visual hierarchy on a phone.
 *
 * This dispatcher narrows on event mode (via flight shape) and forwards
 * to the variant view. Data lives in the Apollo cache from Score; this
 * tab is a pure presentation surface — no mutations, no polling beyond
 * cache-and-network.
 */
const Scorecard = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const online = useOnlineStatus()
  const { data, loading, refetch } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-and-network',
  })
  // Read-only review surface, but the scores + per-member hcp behind it keep
  // changing (flight members entering, admin corrections). Poll so the card
  // stays truthful without a reload — slowly once the round is locked, since
  // only a TD reopen can change it then.
  useLivePoll(refetch, player.polfEvent.isReadOnly ? 60_000 : 15_000, true, {
    idleAfterMs: 5 * 60_000,
    idleIntervalMs: 60_000,
  })

  // Memoised so child hooks don't re-fire every render on the freshly-
  // created `?? []` empty array.
  const flight = useMemo(
    () => data?.currentPlayerFlightScores ?? [],
    [data?.currentPlayerFlightScores]
  )
  // The match, for the Slag/Match toggle. Gated so a non-match event never
  // fires it.
  const matchPlay = Boolean(player.polfEvent.matchPlay)
  const { data: matchData } = useCurrentPlayerFlightMatchesQuery({
    fetchPolicy: 'cache-and-network',
    skip: !matchPlay,
  })
  const matches = matchData?.currentPlayerFlightMatches ?? []
  // Match is the default view when the event has one: it is the competition
  // the round is being played for.
  const [view, setView] = useState<'match' | 'strokes'>('match')

  // The union across entries, not one entry's rows. On a split round no single
  // entry holds the whole card — a team entry covers only its one-ball block —
  // so reading flight[0] rendered a six-hole scorecard. Hole metadata (par,
  // index) is course-level and identical wherever it appears, so the first row
  // seen for a hole describes it. Every other format gives every entry every
  // hole, where the union is that same list.
  const holes = useMemo<HoleScoreFragment[]>(() => {
    const byHole = new Map<number, HoleScoreFragment>()
    for (const entry of flight) {
      for (const row of entry.scores ?? []) {
        if (!byHole.has(row.hole.number)) byHole.set(row.hole.number, row)
      }
    }
    return [...byHole.values()].sort((a, b) => a.hole.number - b.hole.number)
  }, [flight])

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster scorekort…
      </section>
    )
  }
  // Only an empty cache is an error — a failed poll keeps the last good card on
  // screen (same care as PlayerShell).
  if (!flight.length || !holes.length) {
    return online ? (
      <section className="px-4 py-8 text-center text-flag">
        Fant ikke scorekortet ditt.
      </section>
    ) : (
      <section className="px-4 py-8 text-center text-ink-500">
        Ingen nett. Scorekortet lastes så snart du er tilkoblet igjen.
      </section>
    )
  }

  // Same gate the Score page uses: isBestBallFlight is a whole-round test and
  // would answer true across all 18 holes of a split round, which gets its own
  // view: one card per block, because a single grid has two thirds of its
  // cells structurally empty.
  const segmented = isSegmented(player.polfEvent.mode)
  let card = <IndividualView player={player} flight={flight} holes={holes} />
  if (segmented) {
    card = <SegmentedView player={player} flight={flight} holes={holes} />
  } else if (!isBestWorst(player.polfEvent.mode) && isBestBallFlight(flight)) {
    card = <BestBallView player={player} flight={flight} holes={holes} />
  }

  if (!matchPlay || matches.length === 0) return card

  return (
    <div className="flex flex-col gap-2">
      <div className="px-4 pt-3">
        <Toggle
          options={[
            { value: 'match', label: 'Match' },
            {
              value: 'strokes',
              // A scramble or foursome has no individual card — the card
              // behind the toggle is the TEAM's. Name the competition, not
              // the unit, same argument as Individuell over Slag.
              label: isOneBallTeamMode(player.polfEvent.mode)
                ? 'Lag'
                : 'Individuell',
            },
          ]}
          value={view}
          onChange={setView}
        />
      </div>
      {view === 'match' ? <MatchView matches={matches} holes={holes} /> : card}
    </div>
  )
}

export default Scorecard
